/**
 * The amount-as-text half of ADR 060 — Money Type presentation: resolve any
 * amount shape to a `Money` and format it through `format()`, or give the
 * em-dash when there is nothing honest to print.
 *
 * Pure and React-free (BL-2004). It lived in the `Money` component module
 * (`@auctogon/core/money-display`, a `'use client'` React file) until a pure
 * isomorphic formatter needed it: admin-isomorphic's `formatLotPrice` may not
 * import React (ADR 054 — Package Layers, ADR 010 — Isomorphic Services).
 * The component module re-exports both functions, so its API is unchanged.
 */

import Decimal from 'decimal.js';

import { fromWire, type MoneyWire } from './codec';
import { isSupportedCurrency } from './currency';
import { format } from './format';
import { Money } from './money';
import { groupThousands } from './whole-units-input';

/** What a missing or unrenderable amount prints as. */
export const MONEY_EM_DASH = '—';

/**
 * Resolve any accepted amount shape to a `Money`, or `null` when there is no
 * renderable amount (missing, blank, malformed, or an unsupported currency).
 *
 * @param value - The amount: a `Money`, a `MoneyWire`, or a bare number / NUMERIC string.
 * @param currency - The pairing currency for a bare amount (ignored otherwise).
 * @returns The `Money`, or null.
 */
export function toDisplayMoney(
    value: Money | MoneyWire | number | string | null | undefined,
    currency: string | null | undefined,
): Money | null {
    if (value == null) return null;
    if (value instanceof Money) return value;
    try {
        if (typeof value === 'object') return fromWire(value);
        if (!isSupportedCurrency(currency)) return null;
        if (typeof value === 'number') {
            return Number.isFinite(value) ? Money.of(String(value), currency) : null;
        }
        // A blank string is "no amount", not $0.00 (Number('') === 0).
        return value.trim() === '' ? null : Money.of(value.trim(), currency);
    } catch {
        // Malformed decimal or unsupported currency: nothing honest to print.
        return null;
    }
}

/**
 * An amount as plain text — for the places it lives inside text (a modal
 * subtitle, a tooltip, a chip label, a table cell built by a pure formatter).
 * Missing / unknown-currency amounts give the em-dash; no currency is ever
 * defaulted.
 *
 * @param value - The amount in any accepted shape.
 * @param currency - The pairing currency for a bare amount (ignored for a `Money` / `MoneyWire`).
 * @param opts.noCents - `true`: whole units only. `'when-whole'`: drop the minor
 *   unit only when the amount has none ("$8,000", but "$2,600.50") — for a figure
 *   that is usually whole but must never be rounded. A no-op for JPY either way.
 * @returns The formatted amount, e.g. `¥1,000` or `$1,000.00`.
 */
export function formatMoneyText(
    value: Money | MoneyWire | number | string | null | undefined,
    currency: string | null | undefined,
    opts: { noCents?: boolean | 'when-whole' } = {},
): string {
    const money = toDisplayMoney(value, currency);
    if (money == null) return MONEY_EM_DASH;
    return format(money, { wholeUnits: opts.noCents ?? false });
}

/**
 * The currency a fee schedule's bare amounts are written in (BUG-1909,
 * BUG-1915). A stored buyer's-premium or seller-commission schedule is plain
 * numbers with no currency of its own, so its describer takes the currency from
 * the caller that knows it. Required (even when null) so no caller can forget it
 * and silently get dollars.
 */
export interface ScheduleCurrencyOption {
    /**
     * ISO 4217 code the schedule is denominated in. `null` / unsupported while
     * it is unknown: amounts then print as a bare grouped number, never under a
     * guessed "$" (ADR 060 — Money Type).
     */
    currency: string | null | undefined;
}

/**
 * A fee-schedule amount (a threshold, floor, cap or flat fee) as text —
 * `5000` → `"$5,000"` / `"¥5,000"`, through `format()` so the minor-unit count
 * comes off the currency. `'when-whole'` keeps the whole-number amounts every
 * surface has always shown ("$5,000", not "$5,000.00") without rounding a
 * fractional one away ("$5,000.50").
 *
 * Unlike {@link formatMoneyText}, an unknown currency prints the grouped number
 * with no sign rather than the em-dash: the amount is known and worth stating,
 * only its sign is not (BUG-1909's rule, shared by both schedule families).
 *
 * One home for the buyer's-premium describers, the seller-commission describers
 * and the house-default BP read card (BUG-1915 folded their two private copies
 * and a module-level USD formatter into this).
 *
 * @param amount - The stored JSONB amount, in major units.
 * @param currency - ISO 4217 code, or null while unknown.
 * @returns The display string.
 */
export function formatScheduleAmount(amount: number, currency: string | null | undefined): string {
    // The JSONB value is already a JS number; Decimal reads its shortest
    // round-trip form, so no float digits are invented on the way to format().
    const exact = new Decimal(amount);
    if (!isSupportedCurrency(currency)) return groupThousands(exact.toFixed());
    return format(Money.of(exact, currency), { wholeUnits: 'when-whole' });
}
